import { IMAGE_CACHE } from './constants'

export const imageCacheUtil = {
  // Generate consistent cache key based on file name (not URL)
  _getCacheKey(url, fileName = null) {
    if (!url) return ''
    
    // For blob/data URLs, return as-is (don't cache these in localStorage)
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      return ''
    }
    
    // Use file name as cache key if provided
    let cacheIdentifier = fileName
    
    // If no file name provided, extract from URL or hash the URL
    if (!cacheIdentifier) {
      try {
        const urlObj = new URL(url)
        const pathParts = urlObj.pathname.split('/')
        const lastPart = pathParts[pathParts.length - 1]
        if (lastPart && lastPart.includes('.')) {
          cacheIdentifier = lastPart
        } else {
          // Fallback to hashing the base URL (without query params)
          const baseUrl = url.split('?')[0]
          let hash = 0
          for (let i = 0; i < baseUrl.length; i++) {
            const char = baseUrl.charCodeAt(i)
            hash = ((hash << 5) - hash) + char
            hash = hash & hash
          }
          cacheIdentifier = 'url_' + Math.abs(hash).toString(36)
        }
      } catch {
        // If URL parsing fails, hash the entire URL
        let hash = 0
        for (let i = 0; i < url.length; i++) {
          const char = url.charCodeAt(i)
          hash = ((hash << 5) - hash) + char
          hash = hash & hash
        }
        cacheIdentifier = 'url_' + Math.abs(hash).toString(36)
      }
    }
    
    return IMAGE_CACHE.PREFIX + 'data_' + cacheIdentifier.replace(/[^a-zA-Z0-9_-]/g, '_')
  },
  
  // Cache actual image data (blob) by file name
  async cacheImage(url, fileName = null) {
    if (!url) return null
    
    // OSS signed URLs have CORS restrictions - cannot fetch as blob from browser
    if (this.isOssSignedUrl(url)) {
      console.log('[ImageCache] Skipping blob cache for OSS URL (CORS blocked):', fileName || url.substring(0, 60))
      return null
    }
    
    try {
      // Fetch the image as blob (only works for non-OSS URLs)
      const response = await fetch(url, {
        mode: 'cors',
        credentials: 'omit'
      })
      if (!response.ok) throw new Error('Failed to fetch image')
      
      const blob = await response.blob()
      
      // Convert blob to base64 for storage
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      console.warn('[ImageCache] Failed to cache image:', error.message)
      return null
    }
  },
  
  // Set cache with 7 days expiry (persists until manual refresh)
  setCachedImage(url, base64Data, fileName = null) {
    if (!url || !base64Data) return
    
    const key = this._getCacheKey(url, fileName)
    if (!key) return // Don't cache blob/data URLs
    
    // Use 7 days expiry - cache persists until manual refresh clears it
    const expiry = Date.now() + (7 * 24 * 60 * 60 * 1000)
    
    try {
      localStorage.setItem(key, JSON.stringify({
        data: base64Data,
        expiry: expiry,
        url: url,
        fileName: fileName // Store file name for reference
      }))
      console.log('[ImageCache] Cached image data for:', fileName || url.substring(0, 60))
    } catch (error) {
      // localStorage might be full
      console.warn('[ImageCache] Failed to store image (storage full?):', error)
    }
  },
  
  // Get cached image data by file name
  getCachedImage(url, fileName = null) {
    if (!url) return null
    
    const key = this._getCacheKey(url, fileName)
    if (!key) return null // blob/data URLs not cached
    
    const cached = localStorage.getItem(key)
    
    if (!cached) return null
    
    try {
      const data = JSON.parse(cached)
      if (Date.now() > data.expiry) {
        localStorage.removeItem(key)
        return null
      }
      return data.data // Returns base64 string
    } catch {
      localStorage.removeItem(key)
      return null
    }
  },
  
  // Clear all cached images
  clearAllCachedImages() {
    const keys = Object.keys(localStorage)
    let count = 0
    keys.forEach(key => {
      if (key.startsWith(IMAGE_CACHE.PREFIX + 'data_')) {
        localStorage.removeItem(key)
        count++
      }
    })
    console.log(`[ImageCache] Cleared ${count} cached images`)
    return count
  },
  
  // Old methods for backward compatibility (timestamp tracking)
  setCacheTimestamp(url, timestamp = Date.now(), fileName = null) {
    if (!url) return
    const key = this._getCacheKey(url, fileName)
    if (!key) return
    
    // Use 7 days expiry - cache persists until manual refresh clears it
    localStorage.setItem(key, JSON.stringify({
      timestamp,
      expiry: Date.now() + (7 * 24 * 60 * 60 * 1000),
      originalUrl: url, // Store the full URL for reference
      fileName: fileName // Store file name
    }))
    console.log('[ImageCache] setCacheTimestamp:', fileName || key.substring(0, 70))
  },

  getCacheTimestamp(url, fileName = null) {
    if (!url) return null
    const key = this._getCacheKey(url, fileName)
    if (!key) return null
    
    const cached = localStorage.getItem(key)
    
    if (!cached) return null
    
    try {
      const data = JSON.parse(cached)
      if (Date.now() > data.expiry) {
        localStorage.removeItem(key)
        return null
      }
      return data.timestamp
    } catch {
      localStorage.removeItem(key)
      return null
    }
  },

  invalidateCache(url, fileName = null) {
    if (!url) return
    const key = this._getCacheKey(url, fileName)
    if (!key) return
    localStorage.removeItem(key)
  },

  invalidateAllCache() {
    const keys = Object.keys(localStorage)
    let count = 0
    keys.forEach(key => {
      if (key.startsWith(IMAGE_CACHE.PREFIX)) {
        localStorage.removeItem(key)
        count++
      }
    })
    console.log(`[ImageCache] invalidateAllCache: removed ${count} entries`)
  },

  // Invalidate cache for specific URLs only
  invalidateUrls(urls) {
    if (!Array.isArray(urls)) return 0
    
    let count = 0
    urls.forEach(url => {
      if (url) {
        const key = this._getCacheKey(url)
        if (key && localStorage.getItem(key)) {
          localStorage.removeItem(key)
          count++
          console.log('[ImageCache] invalidateUrls REMOVED:', key.substring(0, 70))
        }
      }
    })
    
    console.log(`[ImageCache] invalidateUrls: removed ${count} entries`)
    return count
  },

  getCachedUrl(url, options = {}) {
    if (!url) return ''
    
    // For blob/data URLs, return as-is
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      return url
    }
    
    const fileName = options.fileName || null
    const cacheKey = this._getCacheKey(url, fileName)
    const timestamp = this.getCacheTimestamp(url, fileName)
    const forceRefresh = options.forceRefresh || false
    
    // DEBUG: Log cache lookup
    console.log('[ImageCache] getCachedUrl:', {
      fileName: fileName || 'N/A',
      originalUrl: url.substring(0, 60),
      cacheKey: cacheKey?.substring(0, 60),
      hasTimestamp: !!timestamp,
      isOss: this.isOssSignedUrl(url),
      forceRefresh
    })
    
    // For OSS signed URLs
    if (this.isOssSignedUrl(url)) {
      // If not cached or expired or force refresh, return URL as-is (will get fresh from API)
      if (!timestamp || forceRefresh) {
        console.log('[ImageCache] OSS URL not cached or forced refresh, returning as-is')
        return url
      }
      
      // URL is cached and valid, add timestamp to prevent browser caching of old signature
      try {
        const urlObj = new URL(url)
        urlObj.searchParams.set('_t', timestamp)
        console.log('[ImageCache] Returning cached OSS URL with timestamp')
        return urlObj.toString()
      } catch {
        return url
      }
    }
    
    // For regular URLs, add cache-busting timestamp if cached
    try {
      const urlObj = new URL(url)
      const cacheBuster = timestamp || Date.now()
      urlObj.searchParams.set('_t', cacheBuster)
      return urlObj.toString()
    } catch {
      return url
    }
  },

  // Check if URL is an OSS signed URL
  isOssSignedUrl(url) {
    return url && url.includes('x-oss-expires')
  },

  // Invalidate all OSS signed URLs from cache
  invalidateAllOssUrls() {
    const keys = Object.keys(localStorage)
    let count = 0
    keys.forEach(key => {
      if (key.startsWith(IMAGE_CACHE.PREFIX)) {
        try {
          const data = JSON.parse(localStorage.getItem(key))
          const url = data.originalUrl || key.replace(IMAGE_CACHE.PREFIX, '')
          if (this.isOssSignedUrl(url)) {
            localStorage.removeItem(key)
            count++
          }
        } catch {
          localStorage.removeItem(key)
          count++
        }
      }
    })
    console.log(`[ImageCache] Invalidated ${count} OSS URLs`)
    return count
  },

  // Refresh cache entry with new URL (useful when image is updated)
  refreshCache(oldUrl, newUrl) {
    if (!oldUrl || !newUrl) return
    
    // Invalidate old cache entry
    this.invalidateCache(oldUrl)
    
    // Set new cache entry
    this.setCacheTimestamp(newUrl)
    
    console.log('[ImageCache] Refreshed cache:', oldUrl.substring(0, 50), '->', newUrl.substring(0, 50))
  },

  // Clean up expired cache entries
  cleanupExpired() {
    const keys = Object.keys(localStorage)
    let cleaned = 0
    keys.forEach(key => {
      if (key.startsWith(IMAGE_CACHE.PREFIX)) {
        try {
          const data = JSON.parse(localStorage.getItem(key))
          if (Date.now() > data.expiry) {
            localStorage.removeItem(key)
            cleaned++
          }
        } catch {
          // Invalid data, remove it
          localStorage.removeItem(key)
          cleaned++
        }
      }
    })
    console.log(`[ImageCache] Cleaned up ${cleaned} expired entries`)
    return cleaned
  },

  // Get cache statistics
  getCacheStats() {
    const keys = Object.keys(localStorage)
    let totalSize = 0
    let imageCount = 0
    let ossCount = 0
    let expiredCount = 0
    
    keys.forEach(key => {
      if (key.startsWith(IMAGE_CACHE.PREFIX)) {
        imageCount++
        const value = localStorage.getItem(key)
        totalSize += key.length + value.length
        
        try {
          const data = JSON.parse(value)
          const url = data.originalUrl || key.replace(IMAGE_CACHE.PREFIX, '')
          if (this.isOssSignedUrl(url)) {
            ossCount++
          }
          if (Date.now() > data.expiry) {
            expiredCount++
          }
        } catch {
          expiredCount++
        }
      }
    })
    
    return {
      totalEntries: imageCount,
      ossEntries: ossCount,
      expiredEntries: expiredCount,
      estimatedSizeKB: Math.round(totalSize / 1024 * 100) / 100,
      localStorageUsedPercent: Math.round((totalSize / (5 * 1024 * 1024)) * 100 * 100) / 100 // Assuming 5MB limit
    }
  },

  // Warm cache with multiple URLs at once (useful for lists)
  // Accepts array of objects with {url, fileName} or just URLs
  warmCache(items, forceOverwrite = false) {
    if (!Array.isArray(items)) return 0
    
    let count = 0
    let skipped = 0
    items.forEach(item => {
      // Support both object format {url, fileName} and plain URL string
      const url = typeof item === 'object' ? item.url : item
      const fileName = typeof item === 'object' ? item.fileName : null
      
      if (url) {
        // For OSS signed URLs, only cache timestamp metadata (not blob data due to CORS)
        if (this.isOssSignedUrl(url)) {
          const existingTimestamp = this.getCacheTimestamp(url, fileName)
          if (forceOverwrite || !existingTimestamp) {
            this.setCacheTimestamp(url, Date.now(), fileName)
            count++
            console.log('[ImageCache] warmCache: Cached timestamp for OSS URL:', fileName || url.substring(0, 50))
          } else {
            skipped++
          }
        } else {
          // For non-OSS URLs, cache normally
          const cacheKey = this._getCacheKey(url, fileName)
          
          // If forceOverwrite, always cache (used after manual refresh)
          if (forceOverwrite) {
            this.setCacheTimestamp(url, Date.now(), fileName)
            count++
          } else {
            // Otherwise, only cache if not already cached
            const existingTimestamp = this.getCacheTimestamp(url, fileName)
            if (!existingTimestamp) {
              this.setCacheTimestamp(url, Date.now(), fileName)
              count++
            } else {
              skipped++
            }
          }
        }
      }
    })
    
    console.log(`[ImageCache] warmCache: ${count} new, ${skipped} already cached, total: ${items.length}, forceOverwrite: ${forceOverwrite}`)
    return count
  }
}
